import type {
  IncidentCategory,
  IncidentSeverity,
  IncidentSortField,
  IncidentSource,
  IncidentStatus,
} from '@travel-planner/shared/schemas';
import {
  incidentCategoryValues,
  incidentSeverityValues,
  incidentSortFields,
  incidentSourceValues,
  incidentStatusValues,
} from '@travel-planner/shared/schemas';

// URL search params ↔ typed filters for the incidents management page.
// Single-value filters (v1) keep the URL simple; the server input takes
// arrays, so the widget wraps each value in a one-element array.
export type IncidentsSearch = {
  // Deep link to ONE incident: seeds the detail drawer open on load. Operations
  // links a service's red flag straight to the incident behind it, so the two
  // sides of the coin are one click apart.
  incidentId?: string;
  status?: IncidentStatus;
  severity?: IncidentSeverity;
  category?: IncidentCategory;
  source?: IncidentSource;
  search?: string;
  page?: number;
  sortBy?: IncidentSortField;
  sortOrder?: 'asc' | 'desc';
};

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const asOne = <T extends string>(
  values: readonly T[],
  value: unknown
): T | undefined =>
  typeof value === 'string' && (values as readonly string[]).includes(value)
    ? (value as T)
    : undefined;

export const validateIncidentsSearch = (
  search: Record<string, unknown>
): IncidentsSearch => {
  const out: IncidentsSearch = {};
  if (typeof search.incidentId === 'string' && UUID.test(search.incidentId)) {
    out.incidentId = search.incidentId;
  }
  const status = asOne(incidentStatusValues, search.status);
  if (status) out.status = status;
  const severity = asOne(incidentSeverityValues, search.severity);
  if (severity) out.severity = severity;
  const category = asOne(incidentCategoryValues, search.category);
  if (category) out.category = category;
  const source = asOne(incidentSourceValues, search.source);
  if (source) out.source = source;
  if (typeof search.search === 'string' && search.search.trim().length > 0) {
    out.search = search.search;
  }
  const page = Number(search.page);
  if (Number.isFinite(page) && page > 1) out.page = Math.floor(page);
  const sortBy = asOne(incidentSortFields, search.sortBy);
  if (sortBy) out.sortBy = sortBy;
  if (search.sortOrder === 'asc' || search.sortOrder === 'desc') {
    out.sortOrder = search.sortOrder;
  }
  return out;
};
